import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Save, Ban, AlertTriangle, VolumeX, Plus, FileText, Trash2, CalendarClock, UserCheck } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { Toggle, PageHead } from "@/components/kit";
import { ChannelSelect } from "@/components/ChannelSelect";
import { RoleSelect } from "@/components/RoleSelect";

const actionColor = { ban: "#ef4444", warn: "#f97316", mute: "#eab308", unban: "#10b981", kick: "#ef4444", timeout: "#eab308", any: "#a1a1aa" };
const actionIcon = { ban: Ban, warn: AlertTriangle, mute: VolumeX, unban: Shield, kick: Ban };

const rules = [
  { key: "anti_spam", label: "Anti-Spam", desc: "Block repeated / flood messages" },
  { key: "anti_link", label: "Anti-Link", desc: "Remove external links" },
  { key: "anti_invite", label: "Anti-Invite", desc: "Block Discord invite links" },
  { key: "profanity_filter", label: "Profanity Filter", desc: "Filter offensive language" },
];

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TPL_ACTIONS = ["warn", "kick", "ban", "timeout", "any"];

const fieldCls = "mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt";
const labelCls = "text-xs uppercase tracking-wider text-zinc-500";
const cardCls = "bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4";

export default function Moderation() {
  const { guildId, refreshKey } = useGuild();
  const [cfg, setCfg] = useState(null);
  const save = useUnsaved(cfg, setCfg, () => saveRaw());
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ action: "warn", target: "", reason: "" });

  // Templates
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState({ label: "", action: "warn", reason: "", dm_message: "" });

  // Scheduled reports & verification modules
  const [reports, setReports] = useState(null);
  const [verify, setVerify] = useState(null);
  const saveReports = useUnsaved(reports, setReports, () => saveReportsRaw());
  const saveVerify = useUnsaved(verify, setVerify, () => saveVerifyRaw());

  const load = () => {
    api.get(`/guilds/${guildId}/modules/moderation`).then((r) => setCfg(r.data));
    api.get(`/guilds/${guildId}/modlogs`).then((r) => setLogs(r.data));
    api.get(`/guilds/${guildId}/mod-templates`).then((r) => setTemplates(r.data));
    api.get(`/guilds/${guildId}/modules/reports`).then((r) => setReports(r.data));
    api.get(`/guilds/${guildId}/modules/verification`).then((r) => setVerify(r.data));
  };
  useEffect(load, [guildId, refreshKey]);

  const saveRaw = async () => { await api.put(`/guilds/${guildId}/modules/moderation`, { enabled: cfg.enabled, config: cfg.config }); toast.success("Moderation settings saved"); };
  const addAction = async () => {
    if (!form.target) return toast.error("Enter a target user");
    await api.post(`/guilds/${guildId}/modlogs`, form);
    toast.success(`${form.action} applied to ${form.target}`);
    setForm({ action: "warn", target: "", reason: "" });
    api.get(`/guilds/${guildId}/modlogs`).then((r) => setLogs(r.data));
  };

  const addTemplate = async () => {
    if (!tpl.label.trim()) return toast.error("Enter a template label");
    try {
      await api.post(`/guilds/${guildId}/mod-templates`, tpl);
      toast.success("Template saved");
      setTpl({ label: "", action: "warn", reason: "", dm_message: "" });
      api.get(`/guilds/${guildId}/mod-templates`).then((r) => setTemplates(r.data));
    } catch { toast.error("Failed to save template"); }
  };
  const delTemplate = async (id) => {
    await api.delete(`/guilds/${guildId}/mod-templates/${id}`);
    setTemplates((t) => t.filter((x) => x.id !== id));
  };

  const saveReportsRaw = async () => {
    await api.put(`/guilds/${guildId}/modules/reports`, { enabled: reports.enabled, config: reports.config });
    toast.success("Weekly report settings saved");
  };
  const saveVerifyRaw = async () => {
    await api.put(`/guilds/${guildId}/modules/verification`, { enabled: verify.enabled, config: verify.config });
    toast.success("Verification gate saved");
  };

  if (!cfg) return <p className="text-zinc-500">Loading…</p>;
  const c = cfg.config;
  const set = (k, v) => setCfg({ ...cfg, config: { ...c, [k]: v } });
  const setR = (k, v) => setReports({ ...reports, config: { ...reports.config, [k]: v } });
  const setV = (k, v) => setVerify({ ...verify, config: { ...verify.config, [k]: v } });

  return (
    <div>
      <PageHead title="Moderation" desc="Automated filters and manual moderation tools.">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm"><span className="text-zinc-400">Auto-Mod</span><Toggle testid="mod-enabled" checked={cfg.enabled} onChange={(v) => setCfg({ ...cfg, enabled: v })} /></div>
          <button data-testid="mod-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
        </div>
      </PageHead>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={cardCls}>
          <h3 className="font-head font-semibold flex items-center gap-2"><Shield className="w-4 h-4 text-volt" /> Auto-Mod Rules</h3>
          {rules.map((r) => (
            <div key={r.key} className="flex items-center justify-between">
              <div><div className="text-sm font-medium">{r.label}</div><div className="text-xs text-zinc-500">{r.desc}</div></div>
              <Toggle testid={`rule-${r.key}`} checked={!!c[r.key]} onChange={(v) => set(r.key, v)} />
            </div>
          ))}
          <div className="pt-2"><label className={labelCls}>Max mentions per message</label>
            <input type="number" value={c.max_mentions} onChange={(e) => set("max_mentions", +e.target.value)} className={fieldCls} /></div>
          <div><label className={labelCls}>Warn threshold (auto-ban)</label>
            <input type="number" value={c.warn_threshold} onChange={(e) => set("warn_threshold", +e.target.value)} className={fieldCls} /></div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h3 className="font-head font-semibold mb-4">Manual Action</h3>
            <div className="flex flex-wrap gap-3 items-end">
              <div><label className={labelCls}>Action</label>
                <select data-testid="action-select" value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} className="mt-1 block bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt">
                  <option value="warn">Warn</option><option value="mute">Mute</option><option value="kick">Kick</option><option value="ban">Ban</option><option value="unban">Unban</option>
                </select></div>
              <div className="flex-1 min-w-[140px]"><label className={labelCls}>Target user</label>
                <input data-testid="action-target" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder="username" className={fieldCls} /></div>
              <div className="flex-1 min-w-[140px]"><label className={labelCls}>Reason</label>
                <input data-testid="action-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="optional" className={fieldCls} /></div>
              <button data-testid="action-submit" onClick={addAction} className="bg-red-500/15 text-red-400 border border-red-500/30 rounded-md px-4 py-2 hover:bg-red-500/25 flex items-center gap-2"><Plus className="w-4 h-4" /> Apply</button>
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h3 className="font-head font-semibold mb-4">Audit Log</h3>
            <div className="space-y-2 max-h-[360px] overflow-y-auto">
              {logs.map((a) => {
                const Icon = actionIcon[a.action] || Shield;
                return (
                  <div key={a.id} data-testid="modlog-row" className="flex items-center gap-3 border-l-2 pl-3 py-2 bg-black/30 rounded-r-md" style={{ borderColor: actionColor[a.action] }}>
                    <Icon className="w-4 h-4 shrink-0" style={{ color: actionColor[a.action] }} />
                    <span className="text-sm font-semibold capitalize w-14" style={{ color: actionColor[a.action] }}>{a.action}</span>
                    <span className="text-sm font-medium">{a.target}</span>
                    <span className="text-sm text-zinc-500 truncate">· {a.reason}</span>
                    <span className="text-xs text-zinc-600 ml-auto shrink-0">{a.moderator}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ===== Warn/Ban Templates ===== */}
      <div className="mt-6 bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="templates-card">
        <div className="flex items-center gap-2 mb-1"><FileText className="w-4 h-4 text-volt" /><h3 className="font-head font-semibold">Warn / Ban Templates</h3></div>
        <p className="text-xs text-zinc-500 mb-4">Reusable one-click reasons. These appear as autocomplete choices in the bot's <code className="text-zinc-400">/warn</code> and <code className="text-zinc-400">/ban</code> commands.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Label</label>
                <input data-testid="tpl-label" value={tpl.label} onChange={(e) => setTpl({ ...tpl, label: e.target.value })} placeholder="e.g. Spamming" className={fieldCls} /></div>
              <div><label className={labelCls}>Applies to</label>
                <select data-testid="tpl-action" value={tpl.action} onChange={(e) => setTpl({ ...tpl, action: e.target.value })} className={fieldCls}>
                  {TPL_ACTIONS.map((a) => <option key={a} value={a}>{a === "any" ? "Any action" : a[0].toUpperCase() + a.slice(1)}</option>)}
                </select></div>
            </div>
            <div><label className={labelCls}>Reason</label>
              <input data-testid="tpl-reason" value={tpl.reason} onChange={(e) => setTpl({ ...tpl, reason: e.target.value })} placeholder="Reason shown in the mod log" className={fieldCls} /></div>
            <div><label className={labelCls}>DM to user (optional)</label>
              <textarea data-testid="tpl-dm" value={tpl.dm_message} onChange={(e) => setTpl({ ...tpl, dm_message: e.target.value })} rows={2} placeholder="Message DMed to the user when this template is used" className={fieldCls} /></div>
            <button data-testid="tpl-add" onClick={addTemplate} className="bg-volt text-volt-ink font-semibold rounded-md px-4 py-2 hover:bg-volt-hover flex items-center gap-2 text-sm"><Plus className="w-4 h-4" /> Add template</button>
          </div>

          <div className="space-y-2">
            <div className={labelCls}>Saved templates ({templates.length})</div>
            {templates.length === 0 && <p className="text-sm text-zinc-600">No templates yet.</p>}
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {templates.map((t) => (
                <div key={t.id} data-testid="tpl-row" className="flex items-start gap-3 border-l-2 pl-3 py-2 bg-black/30 rounded-r-md" style={{ borderColor: actionColor[t.action] || "#a1a1aa" }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{t.label}</span>
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ color: actionColor[t.action] || "#a1a1aa", background: (actionColor[t.action] || "#a1a1aa") + "22" }}>{t.action}</span>
                    </div>
                    {t.reason && <div className="text-xs text-zinc-500 truncate">{t.reason}</div>}
                    {t.dm_message && <div className="text-xs text-zinc-600 truncate italic">DM: {t.dm_message}</div>}
                  </div>
                  <button data-testid="tpl-delete" onClick={() => delTemplate(t.id)} className="text-zinc-500 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ===== Scheduled Reports & Verification Gate ===== */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly reports */}
        {reports && (
          <div className={cardCls} data-testid="reports-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-volt" /><h3 className="font-head font-semibold">Scheduled Weekly Report</h3></div>
              <Toggle testid="reports-enabled" checked={!!reports.enabled} onChange={(v) => setReports({ ...reports, enabled: v })} />
            </div>
            <p className="text-xs text-zinc-500">Post a 7-day analytics + moderation summary to a channel every week.</p>
            <div><label className={labelCls}>Report channel</label>
              <ChannelSelect guildId={guildId} value={reports.config.channel} onChange={(v) => setR("channel", v)} testid="reports-channel" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Day</label>
                <select data-testid="reports-weekday" value={reports.config.weekday} onChange={(e) => setR("weekday", +e.target.value)} className={fieldCls}>
                  {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select></div>
              <div><label className={labelCls}>Hour (UTC, 0-23)</label>
                <input type="number" min={0} max={23} data-testid="reports-hour" value={reports.config.hour} onChange={(e) => setR("hour", Math.max(0, Math.min(23, +e.target.value)))} className={fieldCls} /></div>
            </div>
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-medium">DM the server owner</div><div className="text-xs text-zinc-500">Also send the report to the owner's DMs.</div></div>
              <Toggle testid="reports-dm" checked={!!reports.config.dm_owner} onChange={(v) => setR("dm_owner", v)} />
            </div>
            <button data-testid="reports-save" onClick={saveReports} className="bg-volt text-volt-ink font-semibold rounded-md px-4 py-2 hover:bg-volt-hover flex items-center gap-2 text-sm"><Save className="w-4 h-4" /> Save report settings</button>
          </div>
        )}

        {/* Verification gate */}
        {verify && (
          <div className={cardCls} data-testid="verify-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><UserCheck className="w-4 h-4 text-volt" /><h3 className="font-head font-semibold">Verification Gate</h3></div>
              <Toggle testid="verify-enabled" checked={!!verify.enabled} onChange={(v) => setVerify({ ...verify, enabled: v })} />
            </div>
            <p className="text-xs text-zinc-500">New joiners must click a button to verify they're human before unlocking the server.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Verify channel</label>
                <ChannelSelect guildId={guildId} value={verify.config.channel} onChange={(v) => setV("channel", v)} testid="verify-channel" /></div>
              <div><label className={labelCls}>Verified role</label>
                <RoleSelect guildId={guildId} value={verify.config.verified_role} onChange={(v) => setV("verified_role", v)} testid="verify-role" className="mt-1 w-full" /></div>
            </div>
            <div><label className={labelCls}>Prompt message</label>
              <textarea data-testid="verify-message" value={verify.config.message} onChange={(e) => setV("message", e.target.value)} rows={2} className={fieldCls} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Button label</label>
                <input data-testid="verify-button" value={verify.config.button_label} onChange={(e) => setV("button_label", e.target.value)} className={fieldCls} /></div>
              <div><label className={labelCls}>Kick after (min, 0 = off)</label>
                <input type="number" min={0} data-testid="verify-kick" value={verify.config.kick_after_minutes} onChange={(e) => setV("kick_after_minutes", Math.max(0, +e.target.value))} className={fieldCls} /></div>
            </div>
            <button data-testid="verify-save" onClick={saveVerify} className="bg-volt text-volt-ink font-semibold rounded-md px-4 py-2 hover:bg-volt-hover flex items-center gap-2 text-sm"><Save className="w-4 h-4" /> Save verification gate</button>
          </div>
        )}
      </div>
    </div>
  );
}
